/**
 * Frontend Translation Service
 * Uses backend API for server-side cached translations
 */
import { performanceMonitor } from '../utils/performanceMonitor';
import i18n from '../i18n/i18n';
import logger from '../utils/logger';

// Supported languages for the beta
export const SUPPORTED_LANGUAGES = ['es', 'de'];

// Configuration flags
const DISABLE_MONGODB_LOOKUPS = true; // Set to true to disable MongoDB API calls
const PREFER_STATIC_TRANSLATIONS = true; // Prioritize i18n static translations

// Backend API base URL - handle both local and production correctly
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    // Production URL (e.g., https://api.gauntletdebates.com)
    return envUrl;
  } else {
    // Local development
    return 'http://localhost:5000';
  }
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Helper function to construct API URLs properly, avoiding double /api paths
 */
const buildApiUrl = (endpoint: string): string => {
  const baseUrl = API_BASE_URL;
  const apiPath = `/api${endpoint}`;
  
  // Check if base URL already ends with /api
  if (baseUrl.endsWith('/api')) {
    // Base already has /api, just add the endpoint
    return `${baseUrl}${endpoint}`;
  } else {
    // Base doesn't have /api, add the full path
    return `${baseUrl}${apiPath}`;
  }
};

/**
 * Cache interface for localStorage
 */
interface TranslationCache {
  [key: string]: {
    translation: string;
    timestamp: number;
    expiresAt: number;
  };
}

/**
 * Cache settings
 */
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const CACHE_KEY = 'gauntlet_translations_v2';

/**
 * Get cached translation
 */
function getCachedTranslation(text: string, targetLang: string): string | null {
  try {
    const cache: TranslationCache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    const cacheKey = `${text}:${targetLang}`;
    const cached = cache[cacheKey];
    
    if (cached && cached.expiresAt > Date.now()) {
      logger.info(`📋 Using cached translation: "${text}" -> "${cached.translation}"`);
      return cached.translation;
    }
    
    // Remove expired entry
    if (cached && cached.expiresAt <= Date.now()) {
      delete cache[cacheKey];
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    }
    
    return null;
  } catch (error) {
    logger.warn('Error reading translation cache:', error);
    return null;
  }
}

/**
 * Save translation to cache
 */
function setCachedTranslation(text: string, targetLang: string, translation: string): void {
  try {
    const cache: TranslationCache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    const cacheKey = `${text}:${targetLang}`;
    
    cache[cacheKey] = {
      translation,
      timestamp: Date.now(),
      expiresAt: Date.now() + CACHE_DURATION
    };
    
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    logger.info(`💾 Cached translation: "${text}" -> "${translation}"`);
  } catch (error) {
    logger.warn('Error saving to translation cache:', error);
  }
}

/**
 * Check if a translation exists in the static i18n resources
 */
function getStaticTranslation(text: string, targetLang: string): string | null {
  try {
    // Generate a key from the text (similar to how our translation system works)
    const key = text
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ') // Replace non-alphanumeric with spaces
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
      .substring(0, 50); // Limit length

    if (key && key.length > 0) {
      // Access the resources directly from the i18n store
      const store = i18n.store as any;
      if (store.data[targetLang] && 
          store.data[targetLang]['common'] && 
          store.data[targetLang]['common'][key]) {
        const translation = store.data[targetLang]['common'][key];
        logger.info(`📚 Using static translation: "${text}" -> "${translation}"`);
        return translation;
      }
    }
    
    return null;
  } catch (error) {
    logger.warn('Error checking static translations:', error);
    return null;
  }
}

/**
 * Main translation function - prioritizes static translations, falls back to API
 */
export async function translateText(text: string, targetLang: string): Promise<string> {
  try {
    // Skip translation for English or empty text
    if (targetLang === 'en' || !text || text.trim() === '') {
      return text;
    }

    // Check if language is supported
    if (!SUPPORTED_LANGUAGES.includes(targetLang)) {
      logger.warn(`🚫 Language ${targetLang} not supported in beta`);
      return text;
    }

    // 1. Check static i18n translations first (if enabled)
    if (PREFER_STATIC_TRANSLATIONS) {
      const staticTranslation = getStaticTranslation(text, targetLang);
      if (staticTranslation) {
        performanceMonitor.trackTranslationCacheHit(); // Count as cache hit since it's instant
        return staticTranslation;
      }
    }

    // 2. Check localStorage cache
    const cached = getCachedTranslation(text, targetLang);
    if (cached) {
      performanceMonitor.trackTranslationCacheHit();
      return cached;
    }

    // 3. If MongoDB lookups are disabled, return original text
    if (DISABLE_MONGODB_LOOKUPS) {
      logger.info(`🚫 MongoDB lookups disabled, returning original text: "${text}"`);
      return text;
    }

    // 4. Fall back to backend API (only if MongoDB lookups are enabled)
    logger.info(`🌍 Calling backend API for translation: "${text}" (en -> ${targetLang})`);
    performanceMonitor.trackTranslationApiCall();

    // Call backend API - construct URL properly to avoid double /api paths
    const apiUrl = buildApiUrl(`/translate/single/${encodeURIComponent(text)}/${targetLang}?sourceLang=en`);
    logger.info(`🔗 API URL: ${apiUrl}`);

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Backend translation failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (!result.success || !result.translatedText) {
      throw new Error('Invalid response from backend translation API');
    }

    const translatedText = result.translatedText;

    // Cache the result
    setCachedTranslation(text, targetLang, translatedText);

    logger.info(`✅ Backend translation: "${text}" -> "${translatedText}"`);
    return translatedText;

  } catch (error: any) {
    logger.error(`❌ Translation failed for "${text}":`, error.message);
    return text; // Return original text as fallback
  }
}

/**
 * Batch translation function with chunking for large requests
 */
export async function translateBatch(texts: string[], targetLang: string): Promise<Record<string, string>> {
  try {
    // Filter out texts that are already cached
    const uncachedTexts: string[] = [];
    const results: Record<string, string> = {};

    // Check cache for each text
    texts.forEach(text => {
      const cached = getCachedTranslation(text, targetLang);
      if (cached) {
        results[text] = cached;
      } else {
        uncachedTexts.push(text);
      }
    });

    // If all texts are cached, return immediately
    if (uncachedTexts.length === 0) {
      return results;
    }

    logger.info(`🌍 Batch translating ${uncachedTexts.length} texts via backend API`);

    // Split into smaller chunks to avoid overwhelming the server
    const CHUNK_SIZE = 25; // Reduced from 100 to 25
    const chunks: string[][] = [];
    
    for (let i = 0; i < uncachedTexts.length; i += CHUNK_SIZE) {
      chunks.push(uncachedTexts.slice(i, i + CHUNK_SIZE));
    }

    logger.info(`📦 Processing ${chunks.length} chunks of ${CHUNK_SIZE} texts each`);

    // Process chunks sequentially to avoid rate limiting
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      logger.info(`📋 Processing chunk ${i + 1}/${chunks.length} (${chunk.length} texts)`);

      try {
        // Call backend batch API
        const apiUrl = buildApiUrl('/translate/batch');
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            texts: chunk,
            targetLang,
            sourceLang: 'en'
          })
        });

        if (!response.ok) {
          logger.warn(`⚠️ Chunk ${i + 1} failed with status ${response.status}, falling back to individual translations`);
          
          // Fallback: translate each text individually
          for (const text of chunk) {
            try {
              const translation = await translateText(text, targetLang);
              results[text] = translation;
            } catch (error) {
              logger.warn(`⚠️ Individual translation failed for: "${text}"`);
              results[text] = text; // Use original text as fallback
            }
          }
          continue;
        }

        const result = await response.json();

        if (!result.success || !result.translations) {
          logger.warn(`⚠️ Chunk ${i + 1} returned invalid response, falling back to individual translations`);
          
          // Fallback: translate each text individually
          for (const text of chunk) {
            try {
              const translation = await translateText(text, targetLang);
              results[text] = translation;
            } catch (error) {
              logger.warn(`⚠️ Individual translation failed for: "${text}"`);
              results[text] = text; // Use original text as fallback
            }
          }
          continue;
        }

        // Cache and add successful translations to results
        Object.entries(result.translations).forEach(([text, translation]) => {
          setCachedTranslation(text, targetLang, translation as string);
          results[text] = translation as string;
        });

        logger.info(`✅ Chunk ${i + 1}/${chunks.length} completed: ${Object.keys(result.translations).length} translations`);

        // Add small delay between chunks to avoid overwhelming the server
        if (i < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

      } catch (chunkError: any) {
        logger.warn(`⚠️ Chunk ${i + 1} failed completely:`, chunkError.message);
        
        // Fallback: translate each text individually
        for (const text of chunk) {
          try {
            const translation = await translateText(text, targetLang);
            results[text] = translation;
          } catch (error) {
            logger.warn(`⚠️ Individual translation failed for: "${text}"`);
            results[text] = text; // Use original text as fallback
          }
        }
      }
    }

    const totalTranslated = Object.keys(results).length - (texts.length - uncachedTexts.length);
    logger.info(`✅ Batch translation completed: ${totalTranslated} new translations`);
    return results;

  } catch (error: any) {
    logger.error('❌ Batch translation failed completely:', error.message);
    
    // Ultimate fallback: return original texts for all failed translations
    const fallbackResults: Record<string, string> = {};
    texts.forEach(text => {
      const cached = getCachedTranslation(text, targetLang);
      fallbackResults[text] = cached || text;
    });
    return fallbackResults;
  }
}

/**
 * Check if a language is supported
 */
export function isLanguageSupported(lang: string): boolean {
  return SUPPORTED_LANGUAGES.includes(lang);
}

/**
 * Get list of supported languages
 */
export function getSupportedLanguages(): string[] {
  return [...SUPPORTED_LANGUAGES];
}

/**
 * Clear translation cache
 */
export function clearTranslationCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
    logger.info('🧹 Translation cache cleared');
  } catch (error) {
    logger.warn('Error clearing translation cache:', error);
  }
}

/**
 * Get cache statistics
 */
export function getCacheStats(): { totalEntries: number; totalSize: number } {
  try {
    const cache: TranslationCache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    const entries = Object.keys(cache).length;
    const size = JSON.stringify(cache).length;
    
    return {
      totalEntries: entries,
      totalSize: size
    };
  } catch (error) {
    return { totalEntries: 0, totalSize: 0 };
  }
} 